import * as React from 'react';
import { CheckCircleFilled, CloseCircleFilled, DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { createUseStyles } from 'react-jss';
import {
    groupTrackingEventsForTimeline, getEventName, getDate, getTime, TimelineEvent,
} from '../utils';
import { TrackingEvent } from 'library/Types';
import { ReduxStore } from 'reducers/redux.types';
import GalleryModal from 'components/common/gallery-modal';
import AirplaneIcon from 'assets/airplane.svg';
import StatusPrettyName from 'components/consignments/status-pretty-name';
import { timelineViewStyles } from './timeline-view-styles';

interface TimelineViewProps {
    tracking: TrackingEvent[];
    trackingResult: any;
}

interface GalleryModalData {
    images: string[];
    videos: string[];
}

const useTimelineViewStyles = createUseStyles(timelineViewStyles);

const TimelineView: React.FC<TimelineViewProps> = ({ tracking, trackingResult }) => {
    const { t, i18n } = useTranslation();
    const classes = useTimelineViewStyles();
    const useEventPrettyNameOverridesInTracking = useSelector(
        (state: ReduxStore) => state.master?.config?.use_event_pretty_name_overrides_in_tracking,
    );
    const [isGalleryModalVisible, setIsGalleryModalVisible] = React.useState(false);
    const [galleryModalData, setGalleryModalData] = React.useState<GalleryModalData>({ images: [], videos: [] });
    const [expandedStatuses, setExpandedStatuses] = React.useState<string[]>([]);

    const groupedEvents: TimelineEvent[] = groupTrackingEventsForTimeline(tracking);
    const expectedDeliveryDate = trackingResult?.data?.expected_delivery_date;

    const currentStatusIndex = groupedEvents.reduce((lastIndex, event, index) => {
        return event.isAchieved ? index : lastIndex;
    }, -1);

    const toggleExpanded = (status: string) => {
        if (expandedStatuses.includes(status)) {
            setExpandedStatuses(expandedStatuses.filter((s) => s !== status));
        } else {
            setExpandedStatuses([...expandedStatuses, status]);
        }
    };

    const renderEventDetails = (event: TrackingEvent) => {
        return (
            <div className={classes.detailsContainer}>
                <div className={classes.detailsTrackingNote}>
                    {useEventPrettyNameOverridesInTracking
                        ? <StatusPrettyName status={event.type} />
                        : getEventName(event, t, i18n)}
                </div>

                {event.tracking_note && (
                    <div className={classes.detailsLocation}>
                        {event.tracking_note}
                    </div>
                )}

                {(event.tracking_location_string || event.strOrigin || event.hub_name) && (
                    <div className={classes.detailsLocation}>
                        {event.tracking_location_string || event.strOrigin || event.hub_name}
                    </div>
                )}

                <div className={classes.detailsDateTime}>
                    {getDate(event.event_time || event.strActionDate)}
                    ,
                    {getTime(event.event_time || event.strActionTime)}
                </div>

                {event.type === 'attempted' && event.failure_reason && (
                    <div className={classes.detailsFailureReason}>
                        {event.failure_reason}
                    </div>
                )}

                {event.type === 'delivered' && (event.poc_image || event.premise_image || event.event_proof_images) && (
                    <div className={classes.detailsLinkWrapper}>
                        <a
                            onClick={() => {
                                setIsGalleryModalVisible(true);
                                setGalleryModalData({
                                    images: event.event_proof_images || [event.poc_image || event.premise_image],
                                    videos: [],
                                });
                            }}
                            className={classes.detailsLink}
                        >
                            {t('tracking_view_pod')}
                        </a>
                    </div>
                )}

                {event.type === 'delivered' && event.signature_image && (
                    <div className={classes.detailsLinkWrapper}>
                        <a
                            onClick={() => {
                                setIsGalleryModalVisible(true);
                                setGalleryModalData({
                                    images: [event.signature_image],
                                    videos: [],
                                });
                            }}
                            className={classes.detailsLink}
                        >
                            {t('tracking_view_customer_signature')}
                        </a>
                    </div>
                )}

                {event.type === 'delivered' && (
                    <div className={classes.otpText}>
                        OTP Verified:
                        {' '}
                        {event.otp_success ? 'Yes' : 'No'}
                    </div>
                )}
            </div>
        );
    };

    const renderStatusIcon = (
        isCancelledStatus: boolean,
        isCurrent: boolean,
        isAchieved: boolean,
        normalizedStatus: string,
    ) => {
        if (isCancelledStatus) {
            return <CloseCircleFilled className={classes.statusIcon} />;
        }

        if (isCurrent) {
            return normalizedStatus === 'delivered'
                ? <CheckCircleFilled className={classes.statusIcon} />
                : <div className={classes.statusDotCurrent} />;
        }

        if (isAchieved) {
            return <CheckCircleFilled className={classes.statusIcon} />;
        }

        return <div className={classes.statusDotPending} />;
    };

    return (
        <>
            <div className={classes.timelineContainer}>
                {groupedEvents.map((timelineEvent, index) => {
                    const {
                        status, events, latestEvent, isAchieved,
                    } = timelineEvent;
                    const isCurrent = index === currentStatusIndex;
                    const hasMultipleEvents = events.length > 1;
                    const isExpanded = expandedStatuses.includes(status);
                    const isLastItem = index === groupedEvents.length - 1;
                    const normalizedStatus = status.toLowerCase();
                    const isCancelledStatus = normalizedStatus === 'cancelled';

                    const timelineItemClass = [
                        classes.timelineItem,
                        isLastItem && classes.timelineItemLast,
                    ].filter(Boolean).join(' ');

                    const getVerticalLineModifier = () => {
                        if (isCancelledStatus) {
                            return classes.verticalLineCancelled;
                        }
                        if (isCurrent || !isAchieved) {
                            return classes.verticalLinePending;
                        }
                        return classes.verticalLineAchieved;
                    };

                    const verticalLineClass = [
                        classes.verticalLine,
                        getVerticalLineModifier(),
                    ].filter(Boolean).join(' ');

                    const statusHeaderClass = [
                        classes.statusHeader,
                        (!isAchieved && !isCancelledStatus) && classes.statusHeaderInactive,
                    ].filter(Boolean).join(' ');

                    const statusIconWrapperClass = [
                        classes.statusIconWrapper,
                        isCancelledStatus && classes.statusIconWrapperCancelled,
                        (isAchieved && !isCancelledStatus) && classes.statusIconWrapperAchieved,
                    ].filter(Boolean).join(' ');

                    const latestEventDateClass = [
                        classes.latestEventDate,
                        !hasMultipleEvents && classes.latestEventDateNoMargin,
                    ].filter(Boolean).join(' ');

                    const expandIconClass = [
                        classes.expandToggleIcon,
                        isExpanded && classes.expandToggleIconExpanded,
                    ].filter(Boolean).join(' ');

                    const canRenderEventContent = (isAchieved || isCancelledStatus) && !!latestEvent;

                    return (
                        <div key={status} className={timelineItemClass}>
                            {!isLastItem && (
                                <div className={verticalLineClass} />
                            )}

                            <div className={classes.timelineRow}>
                                <div className={classes.planeIconWrapper}>
                                    {isCurrent && !isCancelledStatus && (
                                        <img src={AirplaneIcon} alt="plane" className={classes.planeIcon} />
                                    )}
                                </div>
                                <div className={statusIconWrapperClass}>
                                    {renderStatusIcon(
                                        isCancelledStatus,
                                        isCurrent,
                                        isAchieved,
                                        normalizedStatus,
                                    )}
                                </div>

                                <div className={classes.contentContainer}>
                                    <div className={statusHeaderClass}>
                                        {status.charAt(0).toUpperCase() + status.slice(1)}
                                        {hasMultipleEvents && (
                                            <div
                                                onClick={() => toggleExpanded(status)}
                                                className={classes.expandToggle}
                                            >
                                                <DownOutlined className={expandIconClass} />
                                            </div>
                                        )}
                                    </div>

                                    {canRenderEventContent && latestEvent && (
                                        <div>
                                            <div className={classes.latestEventTitle}>
                                                {useEventPrettyNameOverridesInTracking
                                                    ? <StatusPrettyName status={latestEvent.type} />
                                                    : getEventName(latestEvent, t, i18n)}
                                            </div>
                                            <div className={latestEventDateClass}>
                                                {getDate(latestEvent.event_time || latestEvent.strActionDate)}
                                                ,
                                                {getTime(latestEvent.event_time || latestEvent.strActionTime)}
                                            </div>

                                            {isExpanded && (
                                                <div className={classes.expandedEventsContainer}>
                                                    {events.map((event) => {
                                                        const eventKeyParts = [
                                                            event.event_time,
                                                            event.strActionDate,
                                                            event.strActionTime,
                                                            event.tracking_note,
                                                            event.tracking_location_string,
                                                            event.hub_name,
                                                            event.failure_reason,
                                                        ];
                                                        const eventKey = eventKeyParts
                                                            .filter(Boolean)
                                                            .join('-')
                                                            || `${status}-${event.type}`;
                                                        return (
                                                            <div key={eventKey}>
                                                                {renderEventDetails(event)}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {normalizedStatus === 'delivered'
                                                && expectedDeliveryDate && (
                                                <div className={classes.eddText}>
                                                    EDD:
                                                    {' '}
                                                    {getDate(expectedDeliveryDate)}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {isGalleryModalVisible && (
                <GalleryModal
                    isVisible={isGalleryModalVisible}
                    imageVideoGalleryData={galleryModalData}
                    handleModalClose={() => { setIsGalleryModalVisible(false); }}
                />
            )}
        </>
    );
};

export default TimelineView;
